import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PAGE_SIZES } from '../../contants/pageSizes';
import {
  changeChoosenCampaigns,
  changeSetCampaignTab,
  changeTableChoosenData,
} from '../../features/createBudget/createBudgetSlice';
import { useAccount } from '../../hooks/createBudgets/SelectCampaigns/useAccount';
import { useCampaigns } from '../../hooks/createBudgets/SelectCampaigns/useCampaigns';
import { usePagination } from '../../hooks/createBudgets/SelectCampaigns/usePagination';
import { useSources } from '../../hooks/createBudgets/SelectCampaigns/useSource';
import { useDebounce } from '../../hooks/useDebounce';
import css from './SelectCampaigns.module.css';
import AccountsCampaignsSection from './components/AccountsCampaignsSection';
import ConfirmationSidebar from './components/ConfirmationSidebar';
import PaginationControls from './components/PaginationControls';
import TabsHeader from './components/TabsHeader';

const SelectCampaigns = ({ handleConfirm, accountIds, isLoadingBudget }) => {
  const tableRef = useRef(null);

  const params = useParams();
  const {
    choosenCampaigns,
    setCampaignsTab,
    accounts,
    campaigns,
    tableChoosenData,
  } = useSelector(state => state.createBudget);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showError, setShowError] = useState(false);
  const [statusCampaign, setStatusCampaign] = useState('');

  const debouncedSearchTerm = useDebounce(searchTerm, 700);

  const {
    dataPaginationAccountTab,
    dataPaginationCampaignTab,
    pageSize,
    currentPage,
    totalItems,
    setDataPaginationAccountTab,
    setDataPaginationCampaignTab,
    handleChangeSelectPageSize,
    handlePageChange,
  } = usePagination({
    tabFromParent: setCampaignsTab,
    statusCampaign,
    debouncedSearchTerm,
  });

  const dispatch = useDispatch();

  useSources();
  const { isLoading: isLoadingAccount, totalItems: totalItemsAccount } = useAccount({
    dataPaginationAccountTab,
    pageSize,
    debouncedSearchTerm,
    setSearchResults,
  });
  const { isLoading: isLoadingCampaign, totalItems: totalItemsCampaign } = useCampaigns({
    dataPaginationCampaignTab,
    pageSize,
    debouncedSearchTerm,
    statusCampaign,
    accountIds,
    setSearchResults,
  });

  useEffect(() => {
    showError && setShowError(false);
  }, [choosenCampaigns]);

  useEffect(() => {
    tableRef && tableRef.current.scrollTo(0, 0);
  }, [setCampaignsTab]);

  useEffect(() => {
    if (setCampaignsTab === 'accounts') {
      setDataPaginationAccountTab({ totalItems: totalItemsAccount });
      
    } else if (setCampaignsTab === 'campaigns') {
      setDataPaginationCampaignTab({ totalItems: totalItemsCampaign });
    }
  }, [
    totalItemsAccount,
    totalItemsCampaign,
  ]);

  const handleTabChange = (tab, e) => {
    setSearchTerm('');
    setSearchResults([]);
    setStatusCampaign('');
    dispatch(changeSetCampaignTab(tab));
  };

  const handleSelectCampaigns = () => {
    dispatch(
      changeChoosenCampaigns([
        ...new Map(
          [...choosenCampaigns, ...tableChoosenData].map(item => [
            item.id,
            item,
          ]),
        ).values(),
      ]),
    );
    setSearchTerm('');

    dispatch(changeTableChoosenData([]));
  };

  const handleSearch = e => {
    setSearchTerm(e?.target?.value ?? '');
  };

  const handleConfirmError = () => {
    setShowError(true);
  };

  return (
    <div className={css.container}>
      <div className={css.wrapper}>
        <div className={css.tabWrapper}>
          <TabsHeader tab={setCampaignsTab} onChange={handleTabChange} />
        </div>

        <AccountsCampaignsSection
          isDataTab={setCampaignsTab === 'data'}
          isLoading={isLoadingAccount || isLoadingCampaign || isLoadingBudget}
          data={
            debouncedSearchTerm.length > 0 || statusCampaign !== ''
              ? searchResults
              : setCampaignsTab === 'accounts'
              ? accounts
              : campaigns
          }
          type={setCampaignsTab}
          search={searchTerm}
          filter={statusCampaign}
          onSearch={handleSearch}
          onChangeFilter={setStatusCampaign}
          onSelectCampaigns={handleSelectCampaigns}
        />

        {setCampaignsTab !== 'data' && (
          <PaginationControls
            pageSizeOptions={PAGE_SIZES}
            pageSize={pageSize}
            currentPage={currentPage}
            totalItems={totalItems}
            classNameContainer={css.paginationContainer}
            classNamePagination={css.pagination}
            onChangePageSize={handleChangeSelectPageSize}
            onChangePage={handlePageChange}
          />
        )}
      </div>

      <ConfirmationSidebar
        tableRef={tableRef}
        showError={showError}
        errorText="Please add some campaigns first"
        buttonTitle={params?.id ? 'Update' : 'Create'}
        onSubmit={choosenCampaigns?.length ? handleConfirm : handleConfirmError}
      />
    </div>
  );
};

export default SelectCampaigns;
